import React, { useRef, useState, useEffect, useMemo } from "react";
import { ColumnInstance } from "react-table";
import { Accordion } from "react-bootstrap";
import { FormattedMessage } from "react-intl";

import { ReactComponent as DisplayIcon } from "assets/images/icons/DisplayIcon.svg";
import { ReactComponent as SortIcon } from "assets/images/icons/SortDown.svg";
import ExpandableSearch from "components/atoms/ExpandableSearch/ExpandableSearch";
import ColumnFilterItem from "./ColumnFilterItem";

type ColumnFilterProps<D extends object = {}> = {
  columns: Array<ColumnInstance<D>>;
  eventContext: string;
  listViewName: string;
};

function ColumnFilter(props: ColumnFilterProps) {
  const { columns, eventContext, listViewName } = props;
  const [showFilter, setShowFilter] = useState(false);
  const [activeAccordion, setActiveAccordion] = useState<string | undefined>(undefined);
  const [searchText, setSearchText] = useState("");
  const filterContainerRef = useRef<HTMLDivElement>(null);

  const filteredColumns = useMemo(() => {
    if (searchText?.trim()?.length) {
      return columns.filter((column) =>
        column.render("Header")?.toString().toLocaleLowerCase().includes(searchText.toLocaleLowerCase()),
      );
    }

    return columns;
  }, [searchText, columns]);

  const groupedColumns = useMemo(() => {
    const data: any = {};
    filteredColumns.forEach((column) => {
      const columnGroup = column.group || "Other";
      if (!data[columnGroup]) {
        data[columnGroup] = [];
      }
      data[columnGroup].push(column);
    });
    return data;
  }, [filteredColumns]);

  useEffect(() => {
    function handleOutsideClick(e: any) {
      if (!filterContainerRef.current?.contains(e.target)) {
        setShowFilter(false);
      }
    }
    window.addEventListener("click", handleOutsideClick);
    return () => {
      window.removeEventListener("click", handleOutsideClick);
    };
  }, []);

  useEffect(() => setSearchText(""), [showFilter]);

  return (
    <div ref={filterContainerRef} className="column-filter">
      <DisplayIcon
        width={25}
        height={25}
        onClick={() => {
          setShowFilter((prev) => !prev);
        }}
      />
      <div className={`filter-content ${showFilter ? "show" : ""}`}>
        <h5 className="mb-3">
          <FormattedMessage id="general.columns" defaultMessage="Columns" />
        </h5>
        <ExpandableSearch alwaysExpanded value={searchText} onChange={(e) => setSearchText(e.target.value)} />
        <div className="content-list-container mt-14">
          {Object.keys(groupedColumns).length > 1 ? (
            <Accordion onSelect={(eventKey) => setActiveAccordion(eventKey as string)} activeKey={activeAccordion}>
              {Object.keys(groupedColumns).map((key) => (
                <div key={key} className="mb-10">
                  <Accordion.Toggle as="h6" eventKey={key}>
                    <div className="d-flex align-items-center">
                      <p className="mb-0 font-weight-bold">{key}</p>
                      <SortIcon
                        className="ml-1"
                        fill="#000"
                        width={8}
                        height={7}
                        transform={`rotate(${activeAccordion === key ? 180 : 0})`}
                      />
                    </div>
                  </Accordion.Toggle>
                  <Accordion.Collapse eventKey={key}>
                    <div className="mt-3">
                      {groupedColumns[key]?.map((column: ColumnInstance) => (
                        <ColumnFilterItem
                          key={column.id}
                          column={column}
                          eventContext={eventContext}
                          listViewName={listViewName}
                        />
                      ))}
                    </div>
                  </Accordion.Collapse>
                </div>
              ))}
            </Accordion>
          ) : (
            <div>
              {filteredColumns?.map((column: ColumnInstance) => (
                <ColumnFilterItem key={column.id} column={column} eventContext={eventContext} listViewName={listViewName} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ColumnFilter;
